import type { AxiosError } from 'axios'

/**
 * Shared TanStack Query retry policy. TanStack's default retries any failed
 * query 3× with exponential backoff — including 429s. Under load that turned a
 * single throttled user into a retry storm that kept the rate bucket pinned
 * (support 00389200). These helpers stop retrying on 429 and honour the
 * server's `Retry-After` for the delay when a retry does happen.
 */

const RATE_LIMITED_STATUS = 429

// Matches TanStack's historical default so non-429 errors behave as before.
const DEFAULT_MAX_RETRIES = 3
const MAX_BACKOFF_MS = 30_000

type HeaderBag = Record<string, unknown> | undefined

// A rejected query reaches retry/retryDelay as one of several shapes: a raw
// AxiosError, an apisauce ApiErrorResponse, or the Mango ApiError body a
// queryFn chose to reject with. Read whichever we were handed.
interface ErrorLike {
  status?: number
  headers?: HeaderBag
  response?: { status?: number; headers?: HeaderBag }
  originalError?: AxiosError
}

const asErrorLike = (error: unknown): ErrorLike =>
  error && typeof error === 'object' ? (error as ErrorLike) : {}

export const getErrorStatus = (error: unknown): number | undefined => {
  const e = asErrorLike(error)
  return e.status ?? e.response?.status ?? e.originalError?.response?.status
}

export const isRateLimitError = (error: unknown): boolean =>
  getErrorStatus(error) === RATE_LIMITED_STATUS

const getHeader = (headers: HeaderBag, name: string): string | undefined => {
  if (!headers) {
    return undefined
  }
  const value = headers[name] ?? headers[name.toLowerCase()]
  return typeof value === 'string' ? value : undefined
}

const getRetryAfterMs = (error: unknown): number | undefined => {
  const e = asErrorLike(error)
  const raw =
    getHeader(e.headers, 'Retry-After') ??
    getHeader(e.response?.headers, 'Retry-After') ??
    getHeader(e.originalError?.response?.headers as HeaderBag, 'Retry-After')
  if (!raw) {
    return undefined
  }
  const seconds = Number(raw)
  if (Number.isFinite(seconds)) {
    return Math.max(0, seconds * 1000)
  }
  // HTTP-date form.
  const at = Date.parse(raw)
  return Number.isNaN(at) ? undefined : Math.max(0, at - Date.now())
}

export const shouldRetryQuery = (
  failureCount: number,
  error: unknown,
): boolean => {
  if (isRateLimitError(error)) {
    return false
  }
  return failureCount < DEFAULT_MAX_RETRIES
}

export const queryRetryDelay = (
  attemptIndex: number,
  error: unknown,
): number => {
  const retryAfter = getRetryAfterMs(error)
  if (retryAfter !== undefined) {
    return retryAfter
  }
  return Math.min(1000 * 2 ** attemptIndex, MAX_BACKOFF_MS)
}

/**
 * Drop-in `defaultOptions.queries` fragment for a QueryClient. Spread it in and
 * override per client as needed (e.g. staleTime).
 */
export const throttleAwareQueryDefaults = {
  retry: shouldRetryQuery,
  retryDelay: queryRetryDelay,
}
